import { useEffect, useState, useCallback } from 'react'
import axios from 'axios'

interface OrgProfile {
  credits: number
  [key: string]: any
}

export function useCredits() {
  const [credits, setCredits] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>(null)

  const orgId = sessionStorage.getItem('orgId') || ''
  const authToken = sessionStorage.getItem('authToken') || ''

  const fetchProfile = useCallback(async () => {
    // If we don't have an orgId, ensure the UI shows 0 credits instead of leaving null
    if (!orgId) {
      setCredits(0)
      return null
    }

    setLoading(true)
    setError(null)
    try {
      // Use the same organizationprofile endpoint used across the app
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${orgId}`
      const resp = await axios.get(url, {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : undefined,
        },
      })

      // Many endpoints return the profile directly under resp.data
      const data: OrgProfile = resp.data || resp.data?.data || {}
      const c = typeof data.credits === 'number' ? data.credits : Number(data.credits || 0)
      setCredits(Number.isFinite(c) ? c : 0)
      return data
    } catch (err) {
      // Log to aid debugging in dev
      // eslint-disable-next-line no-console
      console.error('useCredits: failed to fetch org profile', err)
      setError(err)
      // keep credits as 0 to avoid showing null
      setCredits((prev) => (prev === null ? 0 : prev))
      return null
    } finally {
      setLoading(false)
    }
  }, [orgId, authToken])

  useEffect(() => {
    // fetch on mount
    fetchProfile()
  }, [fetchProfile])

  // Listen for cross-tab updates (localStorage) and visibility changes
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'zynth:credits_updated') {
        fetchProfile()
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchProfile()
      }
    }

    window.addEventListener('storage', onStorage)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [fetchProfile])

  const refresh = useCallback(() => {
    return fetchProfile()
  }, [fetchProfile])

  const ensureCredits = useCallback(
    (required: number) => {
      // return boolean indicating if enough credits
      if (credits === null) return false
      return credits >= required
    },
    [credits]
  )

  const deductOptimistic = useCallback((amount: number) => {
    setCredits((c) => (c === null ? null : Math.max(0, c - amount)))
  }, [])

  const set = useCallback((val: number) => setCredits(val), [])

  return {
    credits,
    loading,
    error,
    refresh,
    ensureCredits,
    deductOptimistic,
    setCredits: set,
  }
}

export default useCredits
